import React, { useState } from 'react';
import { Card, Form, Input, InputNumber, Button, Space, Typography, message } from 'antd';
import { useMutation } from '@apollo/client';
import {
    QUERY_ADDRESS_METADATA,
    UPDATE_ADDRESS_METADATA,
    AddressMetadata,
    QueryAddressMetadataResponse,
    QueryAddressMetadataVariables,
    UpdateAddressMetadataResponse,
    UpdateAddressMetadataVariables,
} from './graphql/addressMetadata';

const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;

const AddressMetadataTestPage: React.FC = () => {
    const [form] = Form.useForm<AddressMetadata>();
    const [addressIdInput, setAddressIdInput] = useState<string>('');
    const [currentRecord, setCurrentRecord] = useState<AddressMetadata | null>(null);

    const [queryAddressMetadata, { loading: querying }] = useMutation<
        QueryAddressMetadataResponse,
        QueryAddressMetadataVariables
    >(QUERY_ADDRESS_METADATA);

    const [updateAddressMetadata, { loading: saving }] = useMutation<
        UpdateAddressMetadataResponse,
        UpdateAddressMetadataVariables
    >(UPDATE_ADDRESS_METADATA);

    const handleSearch = async () => {
        const trimmed = addressIdInput.trim();
        if (!trimmed) {
            message.warning('请输入 addressId');
            return;
        }

        const addressId = Number(trimmed);
        if (Number.isNaN(addressId) || addressId <= 0) {
            message.warning('addressId 必须为正整数');
            return;
        }

        try {
            const { data } = await queryAddressMetadata({
                variables: { addressId },
            });
            if (!data || !data.queryAddressMetadata) {
                setCurrentRecord(null);
                form.resetFields();
                message.info('未查询到对应的地址元数据信息');
                return;
            }

            const record = data.queryAddressMetadata;
            setCurrentRecord(record);
            form.setFieldsValue({
                ...record,
            });
        } catch (error: any) {
            console.error(error);
            message.error(error?.message ?? '查询失败，请稍后重试');
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            if (!currentRecord) {
                message.warning('请先查询并加载地址元数据信息');
                return;
            }

            // 去除 Apollo 自动注入的 __typename 字段
            const { __typename: _t1, ...cleanCurrent } = (currentRecord as any) ?? {};
            const { __typename: _t2, ...cleanValues } = (values as any) ?? {};

            const payload: AddressMetadata = {
                ...(cleanCurrent as AddressMetadata),
                ...(cleanValues as AddressMetadata),
                addressId: currentRecord.addressId,
            };

            const { data } = await updateAddressMetadata({
                variables: {
                    input: payload,
                },
            });

            if (data?.updateAddressMetadata) {
                message.success('保存成功');
            } else {
                message.error('保存失败');
            }
        } catch (error: any) {
            if (error?.errorFields) {
                // 表单校验错误
                return;
            }
            console.error(error);
            message.error(error?.message ?? '保存失败，请稍后重试');
        }
    };

    const handleClear = () => {
        setCurrentRecord(null);
        form.resetFields();
    };

    return (
        <div className="min-h-screen bg-gray-50 flex justify-center items-start py-8">
            <div className="w-4/5">
                <Card>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {/* 第一部分：查询区域 */}
                    <div>
                        <Title level={2}>地址元数据测试工具</Title>
                        <Paragraph type="secondary">
                            通过 addressId 查询并编辑地址元数据信息，仅限开发/测试环境使用。
                        </Paragraph>
                    </div>

                    <Space
                        align="center"
                        style={{
                            width: '100%',
                            padding: '12px 16px',
                            borderRadius: 8,
                            background: '#f5f5f5',
                        }}
                        size="large"
                    >
                        <Space direction="vertical" size={4}>
                            <Text strong>addressId</Text>
                            <Input
                                style={{ width: 240 }}
                                placeholder="请输入 addressId"
                                value={addressIdInput}
                                onChange={(event) => setAddressIdInput(event.target.value)}
                                onPressEnter={handleSearch}
                            />
                        </Space>
                        <Button type="primary" onClick={handleSearch} loading={querying}>
                            搜索
                        </Button>
                    </Space>

                    {/* 第二部分：编辑区域 */}
                    <Card size="small" title="地址元数据详情" extra={
                        <Space>
                            <Button onClick={handleClear}>清除</Button>
                            <Button type="primary" onClick={handleSave} loading={saving}>
                                保存
                            </Button>
                        </Space>
                    }>
                        <Form<AddressMetadata>
                            form={form}
                            layout="vertical"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Form.Item label="ID" name="id">
                                    <InputNumber style={{ width: '100%' }} disabled />
                                </Form.Item>
                                <Form.Item label="Address ID" name="addressId">
                                    <InputNumber style={{ width: '100%' }} disabled />
                                </Form.Item>

                                <Form.Item label="DPV Match Code" name="dpvMatchCode">
                                    <Input />
                                </Form.Item>
                                <Form.Item label="DPV Footnotes" name="dpvFootnotes">
                                    <Input />
                                </Form.Item>

                                <Form.Item label="DPV Vacant" name="dpvVacant">
                                    <Input />
                                </Form.Item>
                                <Form.Item label="DPV CMRA" name="dpvCmra">
                                    <Input />
                                </Form.Item>

                                <Form.Item label="RDI" name="rdi">
                                    <Input />
                                </Form.Item>
                                <Form.Item label="ZIP Type" name="zipType">
                                    <Input />
                                </Form.Item>

                                <Form.Item label="Record Type" name="recordType">
                                    <Input />
                                </Form.Item>
                                <Form.Item label="County Name" name="countyName">
                                    <Input />
                                </Form.Item>

                                <Form.Item label="County FIPS" name="countyFips">
                                    <Input />
                                </Form.Item>
                                <Form.Item label="Precision" name="precision">
                                    <Input />
                                </Form.Item>

                                <Form.Item label="Precision Score" name="precisionScore">
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item label="Address Type Primary" name="addressTypePrimary">
                                    <Input />
                                </Form.Item>

                                <Form.Item label="Classification Source" name="classificationSource">
                                    <Input />
                                </Form.Item>
                                <Form.Item label="Classification Reason" name="classificationReason">
                                    <Input />
                                </Form.Item>

                                <Form.Item label="Source" name="source">
                                    <Input />
                                </Form.Item>
                                <Form.Item label="Verification Method" name="verificationMethod">
                                    <Input />
                                </Form.Item>
                            </div>

                            <Form.Item label="Source Metadata JSON" name="sourceMetadataJson">
                                <TextArea rows={6} />
                            </Form.Item>
                        </Form>
                    </Card>
                </Space>
                </Card>
            </div>
        </div>
    );
};

export default AddressMetadataTestPage;

